// ENG-5901 (Phase 1 of ADR-0018): runtime lint that rejects *literal*
// secrets in a rendered `.mcp.json` before it is written to disk.
//
// Why this exists
// ---------------
// The audit run on 2026-06-02 found `.mcp.json` carrying plaintext
// SLACK_BOT_TOKEN / SLACK_APP_TOKEN / TELEGRAM_BOT_TOKEN / AGT_API_KEY /
// Composio `x-api-key` values — the same secrets that should be `${VAR}`
// placeholders resolved at MCP-launch time from the spawn env, populated
// either by the wrapper sourcing `.env.integrations` or, for channel bot
// tokens under ENG-9350 (`spawn-inject-channel-secrets`), directly via
// `tmux new-session -e` so the value never touches disk. Either way the
// `.mcp.json` invariant this lint enforces is identical: `${VAR}`, never a
// literal (see ADR-0006 §D2 and the channel/Composio templating in this PR).
//
// This module is the *contributor-side* gate: once the writers in
// `claudecode/index.ts` emit `${VAR}` templates, a future copy-paste of
// the old literal pattern is caught at write time (provision/sync) rather
// than shipping to a host and leaking. It scans both stdio `env` blocks
// and `http`/`sse`/`ws` `headers` objects, because Claude Code substitutes
// `${VAR}` in both (verified against the Claude Code MCP docs — expansion
// locations: command, args, env, url, headers).
//
// IMPORTANT — rollout coupling: arming this scan in the write path
// (`safeWriteMcpJson`) while any writer still emits a literal token would
// reject that write and silently kill the channel. The scan must be wired
// in *lockstep* with converting the literal writers to `${VAR}`. This
// module is pure (no fs, no process) so it can be unit-tested and reasoned
// about independently of that wiring.
//
// A templated value (`${SLACK_BOT_TOKEN}`) never matches these patterns,
// so legitimate post-conversion configs pass cleanly.

import type { McpConfig, McpServerEntry } from './mcp-config-guards.js';

/**
 * Value-shape patterns for credentials that must never appear literally in
 * `.mcp.json`. Anchored at the start of the value so a `${VAR}` template
 * (which begins with `$`) can't match.
 *
 * Extending this list is the intended way to cover new credential shapes —
 * keep each entry documented with the provider it guards.
 */
export const LITERAL_SECRET_PATTERNS: ReadonlyArray<{
  readonly name: string;
  readonly re: RegExp;
}> = [
  // Slack bot token — `xoxb-<workspace>-<...>`
  { name: 'slack_bot_token', re: /^xoxb-/ },
  // Slack app-level token (Socket Mode) — `xapp-<...>`
  { name: 'slack_app_token', re: /^xapp-/ },
  // AGT host API key — `tlk_<...>` (see claudecode-plugin-augmented README).
  { name: 'agt_host_api_key', re: /^tlk_/ },
  // Composio / generic api-key prefix — `ak_<...>`
  { name: 'composio_api_key', re: /^ak_/ },
  // Telegram bot token — `<bot id>:AA<...>` (BotFather format). ENG-5901
  // PR 4: the original `\d{10}:AAE` (from the issue AC) was too narrow —
  // live tokens on agt-aws-1 carry `AA` + a varying third character
  // (don/scout/stirling all had AA-not-E tokens the lint and migration
  // missed). Bot ids are 8–12 digits; the token part always starts `AA`.
  { name: 'telegram_bot_token', re: /^\d{8,12}:AA[A-Za-z0-9_-]/ },
  // ENG-5901 extension beyond the original AC's five patterns: a literal
  // JWT (`eyJ...`) is the shape of a leaked AGT_API_KEY, which the
  // value-prefix patterns above would otherwise miss. Header values often
  // carry it behind `Bearer ` (or a copy-pasted `Authorization: Bearer `),
  // so those prefixes are optionally consumed (CodeRabbit #1731).
  // Templates (`Bearer ${AGT_API_KEY}`) and concrete non-secret values
  // (UUIDs, hosts) never put `eyJ` after the prefix, so this stays
  // false-positive-safe inside .mcp.json.
  { name: 'jwt_agt_api_key', re: /^(?:authorization:\s*)?(?:bearer\s+)?eyJ[A-Za-z0-9_-]+\./i },
];

export type LiteralSecretLocation = 'env' | 'header';

export interface LiteralSecretFinding {
  /** Server key in `mcpServers` (e.g. 'slack', 'composio_googledrive'). */
  readonly server: string;
  /** The offending env key or header name (e.g. 'SLACK_BOT_TOKEN'). */
  readonly field: string;
  /** Whether the literal was found in an `env` value or a `headers` value. */
  readonly location: LiteralSecretLocation;
  /** Which pattern matched (from {@link LITERAL_SECRET_PATTERNS}). */
  readonly pattern: string;
}

/** Match a single value against every known literal-secret pattern. */
function matchLiteralSecret(value: string): string | null {
  for (const { name, re } of LITERAL_SECRET_PATTERNS) {
    if (re.test(value)) return name;
  }
  return null;
}

function scanRecord(
  server: string,
  record: Record<string, unknown> | undefined,
  location: LiteralSecretLocation,
  findings: LiteralSecretFinding[],
): void {
  if (!record) return;
  for (const [field, value] of Object.entries(record)) {
    if (typeof value !== 'string') continue;
    const pattern = matchLiteralSecret(value);
    if (pattern) findings.push({ server, field, location, pattern });
  }
}

/**
 * Scan a rendered `.mcp.json` config for literal secrets in any server's
 * `env` block or `headers` object. Pure: returns findings, logs nothing,
 * throws nothing. An empty array means the config is clean.
 */
export function scanConfigForLiteralSecrets(
  config: McpConfig | unknown,
): LiteralSecretFinding[] {
  const findings: LiteralSecretFinding[] = [];
  if (typeof config !== 'object' || config === null) return findings;
  const servers = (config as McpConfig).mcpServers;
  if (typeof servers !== 'object' || servers === null) return findings;

  for (const [server, raw] of Object.entries(servers)) {
    if (typeof raw !== 'object' || raw === null) continue;
    const entry = raw as McpServerEntry;
    scanRecord(server, entry.env, 'env', findings);
    scanRecord(server, entry.headers, 'header', findings);
  }
  return findings;
}

/**
 * The structured, secret-free stderr line emitted when a write is
 * rejected. Names the field and server but NEVER the value — per the
 * "never expose secrets in output" rule. Stable, greppable format so
 * operators can alert on it.
 */
export function formatLiteralSecretRejection(f: LiteralSecretFinding): string {
  return `[mcp-write] [literal-secret-rejected] field=${f.field} server=${f.server} location=${f.location} pattern=${f.pattern}`;
}
